/**
 * Guitar-pedal port auto-detection.
 *
 * Any `PedalModel` with an `autoDetectSubstring` (the Line 6 driver always
 * names its Helix port containing "Helix") never needs the operator to type
 * or pick a port — OPBridge already enumerates every port (`GET
 * /midi/ports`, live since v1.15.13), this just searches that list by name.
 *
 * A model with no `autoDetectSubstring` (an arbitrary device the operator
 * is telling the app about for the first time, no known name to search for)
 * keeps a one-time manual pick from the same list instead — see Connected
 * Devices.
 */

import type { OpbridgeHttpClient } from '../opbridge/httpClient.ts';

/** Returns the first output port whose name contains `substring`
 *  (case-insensitive), or `undefined` when none is found — pedal unplugged
 *  or its driver isn't installed. Callers must render `undefined` as a
 *  loud, honest "not found" state (DEC-124/138), never silently fall back
 *  to the rig default port. */
export async function detectPortByName(client: OpbridgeHttpClient, substring: string): Promise<string | undefined> {
  const { outputs } = await client.midiPorts();
  const needle = substring.toLowerCase();
  return outputs.find((name) => name.toLowerCase().includes(needle));
}

export interface MeshPortMatch { bridgeId: string; port: string }

/** Scans every given (connected) mesh bridge for a port name matching
 *  `substring`, returning the first match — bridge id AND port name — so a
 *  named auto-detect device (the Helix; any future model with its own
 *  `autoDetectSubstring`) resolves BOTH its device port and which physical
 *  machine it's wired to with zero operator choice, per-item bridge
 *  affinity's whole point for a device OPButtons can already recognize by
 *  name. `candidates` order decides the tie-break on the rare case the same
 *  port name exists on more than one machine — callers put the default
 *  bridge first so today's single-bridge behavior stays the common case. */
export async function detectPortAcrossMesh(
  candidates: { bridgeId: string; client: OpbridgeHttpClient }[],
  substring: string,
): Promise<MeshPortMatch | undefined> {
  for (const { bridgeId, client } of candidates) {
    const port = await detectPortByName(client, substring);
    if (port) return { bridgeId, port };
  }
  return undefined;
}
